import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Settings, Zap, Rocket } from "lucide-react";
import { TestCardsSection } from "./TestCardsSection";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/contexts/ProfileContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useToast } from "@/hooks/use-toast";
import { useUserTests } from "@/hooks/useUserTests";
import { supabase } from "@/integrations/supabase/client";
import { extractTextFromFile, validateFileType } from "@/components/job/utils/jdExtraction";
import { generateQuestions } from "@/components/test-creator/utils/questionGeneration";

export function TestCreationCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { subscription } = useSubscription();
  const { toast } = useToast();
  const { tests } = useUserTests();

  // Redirect non-employers away from test creation
  useEffect(() => {
    if (profile && profile.role !== 'employer') {
      console.log('[TestCreationCenter] Redirecting non-employer to dashboard');
      navigate('/dashboard');
      return;
    }
  }, [profile, navigate]);
  
  const [jobDescription, setJobDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingJD, setIsUploadingJD] = useState(false);
  const [isQuickCreating, setIsQuickCreating] = useState(false);
  const [quickCreateProgress, setQuickCreateProgress] = useState(0);
  const [quickCreateStage, setQuickCreateStage] = useState("");

  // Dynamic placeholder text
  const [currentPlaceholder, setCurrentPlaceholder] = useState("");
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [typingIndex, setTypingIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  
  const placeholderTexts = [
    "I want to hire a junior software engineer for my tech consulting firm in London.",
    "I want to hire a senior mechanical engineer for my aluminum processing factory in Mumbai.",
    "I need a marketing manager with 5+ years experience in digital marketing for my e-commerce startup in Toronto."
  ];

  useEffect(() => {
    if (!isTyping) return;
    
    const currentText = placeholderTexts[currentPlaceholderIndex];
    
    if (typingIndex < currentText.length) {
      const typingTimeout = setTimeout(() => {
        setCurrentPlaceholder(prev => prev + currentText[typingIndex]);
        setTypingIndex(typingIndex + 1);
      }, 25);
      
      return () => clearTimeout(typingTimeout);
    } else {
      const pauseTimeout = setTimeout(() => {
        setIsTyping(false);
        setTimeout(() => {
          setCurrentPlaceholder("");
          setTypingIndex(0);
          setCurrentPlaceholderIndex((currentPlaceholderIndex + 1) % placeholderTexts.length);
          setIsTyping(true);
        }, 2000);
      }, 1500);
      
      return () => clearTimeout(pauseTimeout);
    }
  }, [typingIndex, currentPlaceholderIndex, isTyping, placeholderTexts]);

  // Upload JD handler
  const handleUploadJD = () => {
    document.getElementById('jd-upload')?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!validateFileType(file)) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a DOCX file only.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploadingJD(true);
      const extractedContent = await extractTextFromFile(file);
      setJobDescription(extractedContent.text);
      
      toast({
        title: "File Uploaded Successfully",
        description: `Extracted content from ${extractedContent.fileName}`,
      });
    } catch (error: any) {
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to extract text from file",
        variant: "destructive",
      });
    } finally {
      setIsUploadingJD(false);
      // Reset the input
      event.target.value = '';
    }
  };

  // Manual setup handler
  const handleManualSetup = () => {
    if (!user) {
      navigate('/signup', { 
        state: { 
          returnTo: '/test-creator',
          jobDetails: {
            description: jobDescription,
          }
        }
      });
      return;
    }

    navigate('/test-creator', {
      state: {
        jobDetails: {
          description: jobDescription,
        }
      }
    });
  };

  // Generate test handler - goes to JD step like popup
  const handleGenerateTest = async () => {

    if (!jobDescription.trim()) {
      toast({
        title: "Content Required",
        description: "Please enter a job description or upload a JD file to generate a test.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      
      // Enhance job details from the prompt/JD
      const { data: jobData, error: jobError } = await supabase.functions.invoke('enhance-job-details', {
        body: { description: jobDescription.trim() }
      });
      if (jobError) throw jobError;

      // If not authenticated, send to signup with prefilled details
      if (!user) {
        navigate('/signup', {
          state: {
            returnTo: '/test-creator',
            jobDetails: {
              title: jobData.title,
              description: jobData.description,
              location: jobData.location,
              keyResponsibilities: jobData.keyResponsibilities,
              yearsOfExperience: jobData.yearsOfExperience,
              keySkills: jobData.keySkills,
              workPresence: jobData.workPresence,
              workArrangement: jobData.workArrangement || 'Full-Time',
              seniority: jobData.seniority,
              salaryRangeMin: jobData.salaryRangeMin,
              salaryRangeMax: jobData.salaryRangeMax,
              industry: jobData.industry
            }
          }
        });
        return;
      }

      // Navigate to test creator with enhanced job details (JD step)
      navigate('/test-creator', {
        state: {
          jobDetails: {
            title: jobData.title,
            description: jobData.description,
            location: jobData.location,
            keyResponsibilities: jobData.keyResponsibilities,
            yearsOfExperience: jobData.yearsOfExperience,
            keySkills: jobData.keySkills,
            workPresence: jobData.workPresence,
            workArrangement: jobData.workArrangement || 'Full-Time',
            seniority: jobData.seniority,
            salaryRangeMin: jobData.salaryRangeMin,
            salaryRangeMax: jobData.salaryRangeMax,
            industry: jobData.industry
          }
        }
      });
    } catch (error: any) {
      console.error('Generate Test failed:', error);
      toast({
        title: "Error",
        description: error.message || "Test generation failed. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Quick Create handler - chains all steps automatically
  const handleQuickCreate = async () => {

    if (!jobDescription.trim()) {
      toast({
        title: "Content Required",
        description: "Please enter a job description to quick create a test.",
        variant: "destructive",
      });
      return;
    }

    if (!user) {
      navigate('/signup', { 
        state: { 
          returnTo: '/dashboard',
          quickCreate: true,
          jobDescription: jobDescription.trim()
        }
      });
      return;
    }

    try {
      setIsQuickCreating(true);
      setQuickCreateProgress(25);
      setQuickCreateStage("Analyzing job description...");

      // Step 1: Enhance job details
      const { data: jobData, error: jobError } = await supabase.functions.invoke('enhance-job-details', {
        body: { description: jobDescription.trim() }
      });
      if (jobError) throw jobError;

      setQuickCreateProgress(50);
      setQuickCreateStage("Optimizing question distribution...");

      // Step 2: Get question distribution
      const { data: distributionData, error: distributionError } = await supabase.functions.invoke('suggest-question-distribution', {
        body: {
          jobTitle: jobData.title,
          keySkills: jobData.keySkills,
          yearsOfExperience: jobData.yearsOfExperience,
          maxQuestions: 12
        }
      });
      if (distributionError) throw distributionError;

      setQuickCreateProgress(75);
      setQuickCreateStage("Generating questions...");

      // Step 3: Generate questions
      const generatedQuestions = await generateQuestions(
        jobData.title,
        jobData.description,
        jobData.keyResponsibilities,
        jobData.yearsOfExperience,
        jobData.keySkills,
        { skillConfigs: distributionData.skillConfigs }
      );

      setQuickCreateProgress(100);
      setQuickCreateStage("Complete!");

      // Show success toast
      toast({
        title: "Test Created Successfully!",
        description: `Generated ${generatedQuestions.length} questions for your test.`,
      });

      // Navigate to test creator with everything pre-generated
      navigate('/test-creator', {
        state: {
          jobDetails: {
            title: jobData.title,
            description: jobData.description,
            location: jobData.location,
            keyResponsibilities: jobData.keyResponsibilities,
            yearsOfExperience: jobData.yearsOfExperience,
            keySkills: jobData.keySkills,
            workPresence: jobData.workPresence,
            workArrangement: jobData.workArrangement || 'Full-Time',
            seniority: jobData.seniority,
            salaryRangeMin: jobData.salaryRangeMin,
            salaryRangeMax: jobData.salaryRangeMax,
            industry: jobData.industry
          },
          preGeneratedQuestions: generatedQuestions,
          startAtQuestionMaker: true
        }
      });

    } catch (error: any) {
      console.error('Quick Create failed:', error);
      toast({
        title: "Quick Create Failed",
        description: error.message || "Quick test creation failed. Please try manual setup.",
        variant: "destructive",
      });
    } finally {
      setIsQuickCreating(false);
      setQuickCreateProgress(0);
      setQuickCreateStage("");
    }
  };

  return (
    <div className="min-h-screen p-6 space-y-12">
      {/* Test Creation Section */}
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-full max-w-3xl mx-auto space-y-8">
          {/* Plan Status - Subtle top bar */}
          {subscription && (
            <div className="flex justify-center">
              <div className="inline-flex items-center px-4 py-2 bg-muted/50 rounded-full text-sm">
                <span className="text-muted-foreground">
                  {subscription.tier_name} plan
                </span>
                {subscription.tier_name === 'Free' && (
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="ml-3 text-primary hover:text-primary/80 text-sm h-auto p-0"
                    onClick={() => navigate('/billing')}
                  >
                    Upgrade
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Main Header */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-normal text-foreground">
              Hello {profile?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'there'}, {' '}
              {tests.length === 0 ? "let's create your first assessment" : "let's find our next hire"}
            </h1>
          </div>

          {/* Input Section */}
          <div className="space-y-6">
            <div className="relative bg-white rounded-xl border border-border/30 focus-within:border-primary/50 shadow-sm">
              <Textarea
                placeholder={currentPlaceholder || "Describe the role and skills you want to assess..."}
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                className="min-h-[120px] text-base resize-none border-0 bg-transparent rounded-xl px-4 py-4 placeholder:text-muted-foreground/60 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              
              {/* Hidden file input for JD upload */}
              <input
                type="file"
                accept=".docx"
                onChange={handleFileUpload}
                className="hidden"
                id="jd-upload"
              />
              
              {/* Action Buttons inside the box */}
              <div className="flex flex-wrap gap-2 justify-end px-4 pb-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleUploadJD}
                  disabled={isUploadingJD}
                  className="rounded-full px-3 py-1.5 h-auto border-border/40 hover:bg-muted/50 text-xs"
                >
                  <Upload className="h-3 w-3 mr-1.5" />
                  {isUploadingJD ? "Uploading..." : "Upload JD"}
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleManualSetup}
                  className="rounded-full px-3 py-1.5 h-auto border-border/40 hover:bg-muted/50 text-xs"
                >
                  <Settings className="h-3 w-3 mr-1.5" />
                  Manual Setup
                </Button>
                
                <Button 
                  size="sm"
                  onClick={handleGenerateTest}
                  disabled={isLoading || isQuickCreating}
                  className="rounded-full px-4 py-1.5 h-auto bg-secondary hover:bg-secondary-hover text-secondary-foreground text-xs font-medium"
                >
                  <Zap className="h-3 w-3 mr-1.5" />
                  {isLoading ? "Generating..." : "Generate Test"}
                </Button>
                
                <Button 
                  size="sm"
                  onClick={handleQuickCreate}
                  disabled={isLoading || isQuickCreating}
                  className="rounded-full px-4 py-1.5 h-auto bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium"
                >
                  <Rocket className="h-3 w-3 mr-1.5" />
                  {isQuickCreating ? `${quickCreateStage} (${quickCreateProgress}%)` : "Quick Create"}
                </Button>
              </div>
            </div>
          </div>

          {/* Help text */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground/80">
              Need help?{" "}
              <button 
                onClick={() => {
                  if ((window as any).Calendly) {
                    (window as any).Calendly.initPopupWidget({
                      url: 'https://calendly.com/utkarshgupta-testera/30min?hide_event_type_details=1&text_color=4d4d4a&primary_color=e16702'
                    });
                  }
                }}
                className="text-primary hover:text-primary/80 underline font-medium"
              >
                Schedule a Walkthrough With Sales
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Tests Section */}
      <div className="max-w-6xl mx-auto">
        <TestCardsSection />
      </div>
    </div>
  );
}